import moment, { Moment } from 'moment-timezone'
import { TIME_FORMATS } from '@/config/dates.config'
import TXT from '@/messages'

/**
 * Initial Incoming date time
 */
export const initialIncomingDateTime: IncomingDateTime = {
  date: '',
  time: {
    hours: 10,
    minutes: 0,
  },
  withTime: false,
}

/**
 * Format moment object to match Ticketure's utc timestamp format
 * @param incomingMoment - moment object
 * @returns {string} formatted date string
 */
export const momentToUTCTimestamp = (incomingMoment: Moment): string => incomingMoment
  .utc()
  .format()

/**
 * Check if the given time is in the current year based on the provided time zone.
 * @param time
 * @param timeZone
 * @return {boolean} True if the time is in the current year, false otherwise.
 */
export const isThisYear = (time: moment.Moment, timeZone: string): boolean => (
  time.get('year') === moment.tz(moment(), timeZone)
    .get('year')
)

/**
 * Change date string to UTC date string
 * @param incomingFormat - moment.js compliant format string
 * @param date
 */
export const getUTCDateTimeFromString = (incomingFormat: string, date: string): string => {
  const dateTime = moment(date, incomingFormat)
  return dateTime.utc()
    .format()
}

/**
 * Get a localized date-time based on a given time zone. If no date is provided,
 * the function will use the current date-time.
 *
 * @param {string} timeZone - The time zone to convert the date-time to.
 * @param {Date} [date=new Date()] - The date-time to be localized. Defaults to the current date-time.
 * @returns {moment.Moment} - The localized date-time as a moment object.
 */
export const getLocalizedDate = (timeZone: string, date: Date = new Date()): moment.Moment => (
  moment(date)
    .tz(timeZone)
)

/**
 * Get the user's timezone from the browser. On the server this is the server's zone (UTC on Vercel).
 * @returns {string} - IANA timezone name, e.g. 'America/New_York'
 */
export const getUsersTimezone = (): string => moment.tz.guess()

/**
 * Format a sales window offset in a timezone, with the zone abbreviation for that date
 * @param {moment.Moment} offset - offset moment (not mutated)
 * @param {string} timeZone - the timezone to display in
 * @returns {string} - formatted string, e.g. 'Sat, Apr 10th @ 10:00 AM MDT'
 */
export const formatOnSaleDate = (offset: moment.Moment, timeZone: string): string => (
  moment(offset).tz(timeZone)
    .format(TIME_FORMATS.onSaleDateWithZone)
)

/**
 * Retrieves the formatted selected date for the order summary based on the provided active date and time zone.
 *
 * @param {string | null} activeDate - The active date to be formatted, or `null` if no date is selected.
 * @param {string} timeZone - The time zone to be applied when formatting the active date.
 * @returns {string} The formatted date string if `activeDate` is provided, or a predefined
 * empty date-time string if `activeDate` is `null`.
 */
export const getOrderSummarySelectedDate = (
  activeDate: string | null,
  timeZone: string,
): string => (
  !activeDate
    ? TXT.orderSummary.dateTimeEmpty
    : moment(activeDate).tz(timeZone)
      .format(TIME_FORMATS.selectedDate))

/**
 * Returns true if the value is a valid hh:mm time parameter (00:00–24:00).
 * @param {string | null} time
 * @returns {boolean}
 */
export const isValidTimeParam = (time: string | null): boolean => (
  !!time && moment(time, 'HH:mm', true).isValid()
)

/**
 * Formats the given time string using the specified format, or returns null if the input is null.
 *
 * @export
 * @param {string|null} time - The time string to be formatted, or null if no time is provided.
 * @param {string} timeZone - The timezone to use when formatting the time.
 * @returns {string|null} The formatted time string according to the TIME_FORMATS.timeWithAMPM format, or null if the
 * input is null.
 * @example
 * formatTimeToAMPM('2023-04-26T16:00:00.000Z', 'America/Denver')
 * Output: '10:00 AM'
 */
export const formatTimeToAMPM = (time: string | null, timeZone: string): string | null => (
  time
    ? moment(time)
      .tz(timeZone)
      .format(TIME_FORMATS.timeWithAMPM)
    : null
)
